import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { DndContext, closestCorners, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { useOpportunities } from '../hooks/useOpportunities';
import { useAuth } from '../context/AuthContext';
import { ensureWeeklyTouchTasks } from '../services/tasksService';
import OpportunityColumn from '../components/opportunities/OpportunityColumn';
import OpportunityCard from '../components/opportunities/OpportunityCard';
import OpportunityModal from '../components/modals/OpportunityModal';
import OpportunitiesOverview from '../components/opportunities/OpportunitiesOverview';
import { Plus, Search } from 'lucide-react';

export default function Opportunities() {
  const location = useLocation();
  const { opportunities, updateOpportunity } = useOpportunities();
  const { currentUser } = useAuth();
  
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    if (location.state?.tab) return location.state.tab;
    const params = new URLSearchParams(location.search);
    if (params.get('tab')) return params.get('tab');
    return 'overview';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [activeId, setActiveId] = useState(null);

  // Sync activeTab when location state or search params change
  useEffect(() => {
    if (location.state?.tab) {
      setActiveTab(location.state.tab);
    } else {
      const params = new URLSearchParams(location.search);
      if (params.get('tab')) {
        setActiveTab(params.get('tab'));
      }
    }
  }, [location]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  // Columns definition based on active subtab
  const activeColumns = ['New', 'Presented', 'Approved', 'Proposal', 'Submitted'];
  const submissionColumns = ['Submitted', 'Won', 'Lost'];
  const columnsToShow = activeTab === 'active' ? activeColumns : submissionColumns;

  // Run weekly touch task generation on mount
  useEffect(() => {
    ensureWeeklyTouchTasks();
  }, []);

  // Ensure all existing Approved status opportunities are assigned to Andrew Flippo
  useEffect(() => {
    if (opportunities && opportunities.length > 0) {
      opportunities.forEach(opp => {
        if (opp.status === 'Approved' && opp.assignedTo !== 'Andrew Flippo') {
          updateOpportunity(opp.id, { assignedTo: 'Andrew Flippo' });
        }
      });
    }
  }, [opportunities, updateOpportunity]);

  // Filter opportunities by search query
  const searchedOpportunities = useMemo(() => {
    if (!opportunities) return [];
    return opportunities.filter(opp => {
      const query = searchQuery.toLowerCase();
      return (
        (opp.name || '').toLowerCase().includes(query) ||
        (opp.description || '').toLowerCase().includes(query) ||
        (opp.justification || '').toLowerCase().includes(query)
      );
    });
  }, [opportunities, searchQuery]);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const handleDragEnd = (event) => {
    setActiveId(null);
    // Determine readonly logic - adapt for Atlas Command role if needed
    const isReadOnly = false; 
    if (isReadOnly) return;

    const { active, over } = event;
    if (!over) return;

    const oppId = active.id;
    const newStatus = over.id; // The column ID corresponds to the status string

    // Find the dragged opportunity
    const opportunity = opportunities.find(o => o.id === oppId);
    
    // Ensure stage is one of the visible columns in the current tab
    if (opportunity && opportunity.status !== newStatus && columnsToShow.includes(newStatus)) {
      updateOpportunity(oppId, { status: newStatus });
    }
  };

  const handleOpportunityClick = (opp) => {
    setSelectedOpportunity(opp);
  };

  const handleAddOpportunityClick = () => {
    // Open modal in creation mode
    setSelectedOpportunity({ isNew: true, status: activeTab === 'active' ? 'New' : 'Submitted' });
  };

  return (
    <div style={{ height: 'calc(100vh - 2rem)', display: 'flex', flexDirection: 'column' }}>
      
      {/* Header & Controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ color: 'var(--primary)', margin: 0 }}>Opportunities</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>
            Manage and track business development opportunities.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search Input */}
          <div style={{ position: 'relative', width: '250px' }}>
            <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }}>
              <Search size={16} />
            </span>
            <input 
              type="text" 
              placeholder="Search opportunities..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-input"
              style={{ paddingLeft: '2.25rem' }}
            />
          </div>

          {/* Add Opportunity Button */}
          <button 
            onClick={handleAddOpportunityClick}
            className="btn btn-primary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontWeight: 600 }}
          >
            <Plus size={16} /> Add Opportunity
          </button>
        </div>
      </div>

      {/* Subtab Navigation */}
      <div style={{ display: 'flex', borderBottom: '2px solid var(--border-color)', marginBottom: '1.5rem', gap: '1rem' }}>
        <button 
          onClick={() => setActiveTab('overview')}
          style={{
            padding: '0.75rem 1rem',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'overview' ? '3px solid var(--accent)' : '3px solid transparent',
            color: activeTab === 'overview' ? 'var(--primary)' : 'var(--text-secondary)',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: '0.9375rem',
            transition: 'all 0.2s'
          }}
        >
          Overview
        </button>
        <button 
          onClick={() => setActiveTab('active')}
          style={{
            padding: '0.75rem 1rem',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'active' ? '3px solid var(--accent)' : '3px solid transparent',
            color: activeTab === 'active' ? 'var(--primary)' : 'var(--text-secondary)',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: '0.9375rem',
            transition: 'all 0.2s'
          }}
        >
          Active Pipeline
        </button>
        <button 
          onClick={() => setActiveTab('submissions')}
          style={{
            padding: '0.75rem 1rem',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'submissions' ? '3px solid var(--accent)' : '3px solid transparent',
            color: activeTab === 'submissions' ? 'var(--primary)' : 'var(--text-secondary)',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: '0.9375rem',
            transition: 'all 0.2s'
          }}
        >
          Submissions / Closed ({opportunities.filter(o => ['Submitted', 'Won', 'Lost'].includes(o.status)).length})
        </button>
        <button 
          onClick={() => setActiveTab('archived')}
          style={{
            padding: '0.75rem 1rem',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'archived' ? '3px solid var(--accent)' : '3px solid transparent',
            color: activeTab === 'archived' ? 'var(--primary)' : 'var(--text-secondary)',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: '0.9375rem',
            transition: 'all 0.2s'
          }}
        >
          Archive ({opportunities.filter(o => o.status === 'Archived').length})
        </button>
      </div>

      {activeTab === 'overview' ? (
        <OpportunitiesOverview 
          opportunities={searchQuery ? searchedOpportunities : opportunities} 
          onOpportunityClick={handleOpportunityClick} 
        />
      ) : activeTab === 'archived' ? (
        <div className="table-container" style={{ flex: 1, overflowY: 'auto' }}>
          {searchedOpportunities.filter(o => o.status === 'Archived').length === 0 ? (
            <div style={{ textAlign: "center", padding: "3rem 1rem", color: "var(--text-muted)" }}>
              <p>No archived opportunities found.</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Short Desc / Name</th>
                  <th>Est. Value</th>
                  <th>Date Archived</th>
                  <th>Archive Reason</th>
                  <th style={{ width: "160px", textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {searchedOpportunities.filter(o => o.status === 'Archived').map(opp => {
                  const formattedValue = opp.estimatedValue 
                    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(opp.estimatedValue)
                    : 'N/A';
                  return (
                    <tr key={opp.id}>
                      <td>
                        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{opp.shortDesc || opp.name || 'Unnamed Opportunity'}</span>
                      </td>
                      <td style={{ color: 'var(--primary)', fontWeight: 500 }}>
                        {formattedValue}
                      </td>
                      <td>
                        {opp.archivedAt ? new Date(opp.archivedAt).toLocaleDateString() : 'N/A'}
                      </td>
                      <td style={{ fontStyle: 'italic', color: 'var(--text-secondary)' }}>
                        {opp.archiveReason || 'N/A'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                          <button className="btn btn-secondary" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }} onClick={() => handleOpportunityClick(opp)}>
                            Open
                          </button>
                          <button className="btn btn-primary" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', backgroundColor: 'var(--accent)', borderColor: 'var(--accent)' }} onClick={() => updateOpportunity(opp.id, { status: opp.previousStatus || 'New', previousStatus: null, archived: false, archiveReason: null })}>
                            Restore
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        /* Kanban Board */
        <div style={{ 
          display: 'flex', 
          gap: '1.5rem', 
          overflowX: 'auto', 
          paddingBottom: '1rem',
          flex: 1
        }}>
          <DndContext 
            sensors={sensors} 
            collisionDetection={closestCorners} 
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            {columnsToShow.map(status => (
              <OpportunityColumn 
                key={status} 
                id={status} 
                title={status} 
                opportunities={searchedOpportunities.filter(o => o.status === status)}
                onOpportunityClick={handleOpportunityClick}
                isReadOnly={false}
              />
            ))}
            <DragOverlay>
              {activeId ? (
                <OpportunityCard 
                  opportunity={opportunities.find(o => o.id === activeId)} 
                  onClick={() => {}}
                  isReadOnly={true}
                  isDraggingOverlay={true}
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      )}

      {/* Opportunity Add/Edit Modal */}
      {selectedOpportunity && (
        <OpportunityModal 
          opportunity={selectedOpportunity} 
          onClose={() => setSelectedOpportunity(null)} 
        />
      )}

    </div>
  );
}
